import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import * as Sentry from '@sentry/capacitor';
import { useStripe } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';

import { useBusinessLayer, usePaymentMethod } from '@mopla/business-logic';
import { MOBILE_CONTAINER_WIDTH } from '@mopla/constants';
import { ErrorResponseDto, IWhoami } from '@mopla/data-models';
import {
	MplBottomSheet,
	MplButton,
	MplTopSectionHeader,
	StyledSafeAreaHeader,
} from '@mopla/ui';
import { isValidEmail } from '@mopla/utils';

import { usePaymentStepStore } from '../../stores/usePaymentStepStore';
import { IBankAccount, ITrueLayerReport } from '../../types';

import { CheckoutStep } from './CheckoutStep';
import { ErrorStep } from './ErrorStep';
import { LoadingStep } from './LoadingStep';
import { NoAccountsStep } from './NoAccountsStep';
import { SuccessStep } from './SuccessStep';
import { TSepaPaymentStep } from './types';
import { ValidationStep } from './ValidationStep';

const Root = styled('div')({
	position: 'absolute',
	bottom: 0,
	left: 0,
	right: 0,
	zIndex: 1200,
	width: '100%',
	maxWidth: MOBILE_CONTAINER_WIDTH,
	margin: '0 auto',
	height: '100%',
	background: '#fff',
	display: 'flex',
	flexDirection: 'column',
	boxShadow: '0px 0px 24px rgba(0, 0, 0, 0.12)',
});

const ScrollContent = styled('div', {
	shouldForwardProp: (prop) => prop !== 'isVisible',
})<{ isVisible: boolean }>(({ isVisible }) => ({
	flex: 1,
	overflow: 'auto',
	padding: '0 16px 120px',
	visibility: isVisible ? 'hidden' : 'visible',
}));

interface IProps {
	isLoading: boolean;
	isError?: boolean | null;
	accounts?: ITrueLayerReport[];
	onRedirectToTrueLayer: VoidFunction;
	onCancel: VoidFunction;
}

export const AddSepaPaymentMethodFlow: FC<IProps> = ({
	accounts,
	isLoading,
	isError,
	onRedirectToTrueLayer,
	onCancel,
}) => {
	const { api } = useBusinessLayer();
	const { t } = useTranslation('openBanking');
	const stripe = useStripe();
	const { createSepaPaymentMethod, isPaymentMethodSaving } = usePaymentMethod();
	const setPaymentStep = usePaymentStepStore((s) => s.setStep);

	const [hasEmailValidationError, setHasEmailValidationError] = useState(false);
	const [hasAddressValidationError, setHasAddressValidationError] =
		useState(true);
	const [bankAccount, setBankAccount] = useState<IBankAccount>({
		iban: '',
		address: {
			country: 'DE', // assuming the user is from Germany
		},
	});
	const [step, setStep] = useState<TSepaPaymentStep>('wait');

	useEffect(() => {
		if (isError) {
			setStep('error');
		}
	}, [isError]);

	useEffect(() => {
		const isWaitingForInit = step === 'wait' && !isLoading && !accounts;

		if (
			isWaitingForInit ||
			step === 'error' ||
			step === 'checkout' ||
			step === 'success'
		) {
			return;
		}

		if (isLoading && !accounts) {
			setStep('loading');
		} else if (accounts?.length) {
			/** Pre-select the iban if there's only 1 available */
			if (accounts.length === 1 && !bankAccount.iban) {
				setBankAccount({ ...bankAccount, iban: accounts[0].iban });
			}

			setStep('validation');
		} else if (accounts?.length === 0) {
			setStep('noAccounts');
		} else if (!isLoading && !accounts) {
			setStep('error');
		}
	}, [accounts, bankAccount, isLoading, step]);

	const handlePayment = async (bankAccount: IBankAccount) => {
		if (!stripe) {
			return;
		}

		/** Validate required fields before API call */
		if (!bankAccount.iban || !bankAccount.name || !bankAccount.email) {
			Sentry.captureException(
				new Error('Missing required SEPA payment method fields')
			);
			setStep('error');
			return;
		}

		try {
			await createSepaPaymentMethod({
				iban: bankAccount.iban,
				name: bankAccount.name,
				email: bankAccount.email,
				address: bankAccount.address,
			});

			setStep('success');
		} catch {
			setStep('error');
		}
	};

	const getHeaderTitle = () => {
		switch (step) {
			case 'wait':
			case 'error':
			case 'loading':
			case 'noAccounts':
			case 'validation':
				return t('header_title.validation');
			case 'checkout':
			case 'success':
				return t('header_title.checkout');
		}
	};

	const getUser = async () => {
		return await api
			.get('/api/whoami')
			.then((res: IWhoami) => {
				return res;
			})
			.catch((err: ErrorResponseDto) => console.log(err));
	};

	const handleOnBack = () => {
		if (
			step === 'validation' ||
			step === 'noAccounts' ||
			step === 'error' ||
			step === 'success'
		) {
			onCancel();
		}

		if (step === 'checkout') {
			setBankAccount({ ...bankAccount, iban: '' });
			setStep('validation');
		}
	};

	const handleIbanSelection = async () => {
		const user = await getUser();

		setBankAccount({
			...bankAccount,
			name: `${user?.firstName} ${user?.lastName}`,
			email: user?.email,
			address: {
				line1: `${user?.address?.street} ${user?.address?.streetNumber}`,
				city: user?.address?.city,
				postal_code: user?.address?.zipCode,
				country: bankAccount.address.country,
			},
		});

		setStep('checkout');
	};

	const onChangeMethodClick = useCallback(() => {
		setPaymentStep('stripe');
	}, [setPaymentStep]);

	const hasFooter =
		step === 'error' ||
		step === 'noAccounts' ||
		step === 'validation' ||
		step === 'checkout';

	return (
		<Root>
			{step === 'loading' && <LoadingStep />}

			<StyledSafeAreaHeader>
				<MplTopSectionHeader
					title={getHeaderTitle() ?? ''}
					onBack={handleOnBack}
				/>
			</StyledSafeAreaHeader>

			<ScrollContent isVisible={step === 'loading'}>
				{step === 'error' && (
					<ErrorStep
						onRedirectToTrueLayer={onRedirectToTrueLayer}
						onChangeMethodClick={onChangeMethodClick}
						onCancel={onCancel}
					/>
				)}

				{step === 'noAccounts' && (
					<NoAccountsStep handleOnClick={onChangeMethodClick} />
				)}

				{step === 'validation' && (
					<ValidationStep
						accounts={accounts}
						bankAccount={bankAccount}
						handleOnChange={setBankAccount}
					/>
				)}

				{(step === 'checkout' || step === 'success') && (
					<CheckoutStep
						bankAccount={bankAccount}
						hasEmailValidationError={hasEmailValidationError}
						hasAddressValidationError={hasAddressValidationError}
						handleOnEmailChange={(e: { target: { value: string } }) => {
							const email: string = e.target.value;

							setBankAccount({ ...bankAccount, email });
							setHasEmailValidationError(!isValidEmail(email));
						}}
						handleOnAddressChange={(event: StripeAddressElementChangeEvent) => {
							setBankAccount({
								...bankAccount,
								address: event?.value?.address,
							});

							setHasAddressValidationError(!event.complete);
						}}
					/>
				)}

				{step === 'success' && <SuccessStep />}
			</ScrollContent>

			<MplBottomSheet open={hasFooter} isStatic testId="sepa-footer-sheet">
				<Box padding="16px 16px 8px">
					{step === 'error' && (
						<MplButton fullWidth onClick={handleOnBack}>
							{t('button.cancel')}
						</MplButton>
					)}
					{step === 'noAccounts' && (
						<MplButton fullWidth onClick={onRedirectToTrueLayer}>
							{t('button.try_another_bank')}
						</MplButton>
					)}
					{step === 'validation' && (
						<MplButton
							fullWidth
							onClick={handleIbanSelection}
							disabled={!bankAccount.iban}
						>
							{t('button.next')}
						</MplButton>
					)}
					{step === 'checkout' && (
						<MplButton
							fullWidth
							onClick={() => handlePayment(bankAccount)}
							showLoader={isPaymentMethodSaving}
							disabled={hasEmailValidationError || hasAddressValidationError}
						>
							{t('button.add')}
						</MplButton>
					)}
				</Box>
			</MplBottomSheet>
		</Root>
	);
};
